import type { ReactNode } from 'react';
import { Box, Heading, Spinner, Stack, Text } from '@chakra-ui/react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth, type AdminRole } from '../hooks/useAuth.js';

interface ProtectedRouteProps {
  children: ReactNode;
  /** When set, at least one of these Entra app roles is required. */
  roles?: AdminRole[];
}

export function ProtectedRoute({ children, roles }: ProtectedRouteProps) {
  const { isAuthenticated, isLoading, user, hasRole } = useAuth();
  const location = useLocation();

  if (isLoading) {
    return (
      <Box display="flex" alignItems="center" justifyContent="center" minH="60vh">
        <Spinner size="xl" color="brand.500" />
      </Box>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  if (roles && roles.length > 0 && !hasRole(...roles)) {
    return (
      <Stack gap={3} align="center" py={16} data-name="access-denied">
        <Heading size="lg">Access denied</Heading>
        <Text color="fg.muted">This page requires the {roles.join(' or ')} role.</Text>
        <Text color="fg.muted" fontSize="sm">
          Your roles: {user?.roles.join(', ') || 'none'}
        </Text>
      </Stack>
    );
  }

  return <>{children}</>;
}
